/**
 * Client for the contact endpoint.
 *
 * The form posts to /api/contact — a Static Web Apps managed function, the
 * one server-side surface of this site. It exists so a submission can reach
 * Denise without any mail credential ever shipping to the browser.
 */

export interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  /** Optional. */
  phone: string;
  company: string;
  message: string;
  /** Honeypot — always empty for a human; filled in by bots. */
  website: string;
  /** Milliseconds the form was on screen before submission. */
  elapsedMs: number;
}

export type ContactResult =
  | { ok: true }
  | { ok: false; message: string; fieldErrors?: Record<string, string> };

const GENERIC_FAILURE = 'Something went wrong sending your message. Please try again in a moment.';

export async function submitContactForm(values: ContactFormValues): Promise<ContactResult> {
  let response: Response;
  try {
    response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    });
  } catch {
    // Offline, DNS, blocked — nothing reached the server.
    return {
      ok: false,
      message: "We couldn't reach the server. Check your connection and try again.",
    };
  }

  let body: { ok?: boolean; error?: string; errors?: Record<string, string> } = {};
  try {
    body = await response.json();
  } catch {
    // A non-JSON response means something upstream answered instead of the
    // function — treat it as a generic failure rather than showing HTML.
  }

  if (response.ok && body.ok) return { ok: true };

  if (response.status === 400 && body.errors) {
    return {
      ok: false,
      message: 'Please check the highlighted fields.',
      fieldErrors: body.errors,
    };
  }

  if (response.status === 429) {
    return {
      ok: false,
      message: body.error ?? 'Too many messages just now. Please try again in a little while.',
    };
  }

  return { ok: false, message: body.error ?? GENERIC_FAILURE };
}
